import React from "react";
import LegalPage from "./LegalPage";

const H2 = ({ children }) => (
  <h2 className="font-condensed font-bold text-white text-2xl mt-10 mb-3">
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="font-condensed font-bold text-white text-lg mt-6 mb-2">
    {children}
  </h3>
);

export default function PrivacyPolicyPage() {
  return (
    <LegalPage
      testId="privacy-page"
      title="Privacy Policy"
      updated="February 15, 2026"
      canonicalPath="/privacy"
      description="Privacy Policy for Gadget Guy Media — how we collect, use, and protect information from visitors, clients, and users of gadgetguymedia.com and our products."
    >
      <p>
        Gadget Guy Media LLC (&ldquo;<strong>Gadget Guy Media</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
        privacy. This Privacy Policy explains what information we collect when you visit{" "}
        <strong>gadgetguymedia.com</strong> (the &ldquo;Site&rdquo;) or use any of our services, how we use it, and the
        choices you have.
      </p>

      <H2>1. Information We Collect</H2>
      <H3>Information you give us</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Contact form submissions</strong> — name, email address, project type, company (optional),
          and any message you send us.
        </li>
        <li>
          <strong>Lead capture on the digital business card</strong> (<code>/robert</code>) — first name and
          email address.
        </li>
        <li>
          <strong>Client communications</strong> — anything you share by email, phone, video call, or scheduling
          tool while we&apos;re working together.
        </li>
      </ul>

      <H3>Information collected automatically</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Scan &amp; visit analytics</strong> — user agent (browser), referring URL, source parameter
          (e.g., &ldquo;print&rdquo; for QR codes), and timestamp when you load certain pages.
        </li>
        <li>
          <strong>Cookies and similar technologies</strong> — used by our analytics providers (see below) to
          understand how the Site is used.
        </li>
      </ul>

      <H2>2. How We Use Information</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Respond to inquiries and provide the services you request.</li>
        <li>Send transactional emails (confirmations, digest summaries).</li>
        <li>Improve the Site&apos;s content, performance, and security.</li>
        <li>Comply with legal obligations and enforce our Terms.</li>
      </ul>
      <p>
        We do <strong>not</strong> sell your personal information. We do not use your data to train any
        third-party AI model.
      </p>

      <H2>3. Third-Party Services</H2>
      <p>
        We use trusted vendors to run the Site and our services. Each has its own privacy policy:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>SendGrid (Twilio)</strong> — transactional email delivery.
        </li>
        <li>
          <strong>Google Tag Manager / Google Analytics</strong> — website analytics.
        </li>
        <li>
          <strong>PostHog</strong> — product analytics.
        </li>
        <li>
          <strong>MongoDB</strong> — encrypted database hosting.
        </li>
        <li>
          <strong>Emergent</strong> — application hosting infrastructure.
        </li>
      </ul>

      <H2>4. Cookies</H2>
      <p>
        The Site uses first-party and third-party cookies for analytics and to remember your preferences
        (e.g., the stats dashboard password on the private admin route). You can disable cookies in your
        browser, but some features may not work as intended.
      </p>

      <H2>5. Data Retention</H2>
      <p>
        We retain contact form submissions and lead records for as long as necessary to respond to your
        inquiry and, if you become a client, for the duration of our engagement plus a reasonable period
        thereafter. Analytics data is retained according to each provider&apos;s default retention window.
      </p>

      <H2>6. Your Rights</H2>
      <p>
        Depending on where you live, you may have the right to access, correct, delete, or export the
        personal information we hold about you, and to object to or restrict certain processing. To exercise
        any of these rights, email{" "}
        <a
          href="mailto:info@gadgetguymedia.com"
          className="text-[#28C8F0] hover:underline"
        >
          info@gadgetguymedia.com
        </a>
        .
      </p>

      <H2>7. Children</H2>
      <p>
        The Site is not directed to children under 13, and we do not knowingly collect personal information
        from children.
      </p>

      <H2>8. Security</H2>
      <p>
        We use industry-standard measures (HTTPS, encrypted database connections, password-protected admin
        routes, minimal data collection) to protect your information. No system is 100% secure, and we cannot
        guarantee absolute security.
      </p>

      <H2>9. Changes to This Policy</H2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we&apos;ll change the &ldquo;Last updated&rdquo;
        date at the top of this page. Material changes will be highlighted at the top of the Site.
      </p>

      <H2>10. Contact</H2>
      <p>
        Questions about this Privacy Policy? Reach out at{" "}
        <a href="mailto:info@gadgetguymedia.com" className="text-[#28C8F0] hover:underline">
          info@gadgetguymedia.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
